import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useAuditReadiness } from 'src/pages/Roadmap_v2/roadmap.hooks'
import { normalizeByControlId } from 'src/Utils/globalHelpers'
import { useModalState } from 'src/Modal/ModalStateContext'
import { getRoadmapModalTitle } from 'src/pages/Roadmap_v2/smartchecks/RoadmapSmartChecks.helpers'
import { ROADMAP_MODAL } from 'src/pages/Roadmap_v2/roadmap.constants'
import { AuditBotControlsDescription } from 'src/pages/AuditBot/modals/AuditBotControls/AuditBotControlsModal.components'
import { useControlsWithAuditId } from 'src/components/async/model/control/useControls'
import isEqual from 'lodash/isEqual'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { useDateLimit } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useRefetchCallback } from 'src/pages/Roadmap_v2/smartchecks/hooks/useRefetchCallback'
import { useInAudit } from '../../../../context/AuditContext'
import { ModalFormId } from '../../ModalForm'
import { DateRange } from '../../../../pages/AuditBot/AuditBot.hooks'
import {
  AuditScanModalContextType,
  ButtonConfig,
  ControlDisplay,
  UseAuditScanModalType,
} from './AuditScanModal.constants'
import {
  createControlTestMap,
  getFilteredControlIds,
  getFilteredControls,
} from './AuditScanModal.helpers'
import { AuditScanModalContext } from './AuditScanModal.context'

export const useAuditScanModalContext = (
  calculateStaleness: boolean,
): AuditScanModalContextType & {
  isLoading: boolean
} => {
  // --------------- get context state ---------------
  const {
    auditId,
    selectedTests,
    controlsFilter,
    controlsPreselect,
    setFilteredControls,
    setPreselectedControlIds,
    controlTestMap,
    setControlTestMap,
    singleControlId,
    setDateLimit,
  } = useContext(AuditScanModalContext)

  // --------------- Get controls for the current audit ---------------
  const { data: allControls, isLoading: isAllControlsLoading } =
    useControlsWithAuditId(auditId)

  const { data: dateLimitData, isLoading: isDateLimitLoading } = useDateLimit()

  // we want to use the auditReadiness API instead of the smartChecks API because it will include all of the test results.
  // this can be optimized in the future if needed by limiting the control test options to only the passed in tests. the trade off is this could result in more requests - depends on how this feature is being used.
  const {
    data: auditReadinessData,
    isLoading: isAuditReadinessLoading,
    mutate,
  } = useAuditReadiness({
    auditId,
    relevantTests: [
      CONTROL_TEST_NAMES.COMPLETENESS,
      CONTROL_TEST_NAMES.POLICY_MATCH,
      CONTROL_TEST_NAMES.SPOT_CHECK,
    ],
    calculateStaleness,
  })

  const auditReadinessLookup = useMemo(() => {
    if (!auditReadinessData) return {}

    return normalizeByControlId(auditReadinessData.getAuditReadinessList())
  }, [auditReadinessData])

  // --------------- Update state for controlTestMap, Preselect, Filtered Controls as needed ---------------
  // ----- controlTestMap
  useEffect(() => {
    // create our controlTestMap lookup according to the passed in selectedTests
    // cases to consider
    // what do we do if all tests that have been ran are passing for a given control but not all tests have been ran?
    // eg passing for ControlMatchesPolicy but hasn't ran SpotCheck
    const newControlTestMap = createControlTestMap(
      selectedTests,
      auditReadinessLookup,
    )
    if (!isEqual(controlTestMap, newControlTestMap))
      setControlTestMap(() =>
        createControlTestMap(selectedTests, auditReadinessLookup),
      )
  }, [auditReadinessLookup, controlTestMap, selectedTests, setControlTestMap])

  useRefetchCallback(mutate, 'AuditScanModal')

  useEffect(() => {
    setFilteredControls(() => {
      if (singleControlId) {
        const control = allControls
          ?.getItemsList()
          .find(
            (control: Control) =>
              control.getId() === singleControlId ||
              control.getMigratedControlId() === singleControlId,
          )
        return control ? [control] : []
      } else {
        const filteredControls = getFilteredControls(
          allControls?.getItemsList() || [],
          controlTestMap,
          controlsFilter,
        )
        setPreselectedControlIds(() =>
          getFilteredControlIds(
            filteredControls,
            controlTestMap,
            controlsPreselect,
          ),
        )
        return filteredControls
      }
    })
  }, [
    allControls,
    controlTestMap,
    controlsFilter,
    controlsPreselect,
    setFilteredControls,
    setPreselectedControlIds,
    singleControlId,
  ])

  useEffect(() => {
    const date = dateLimitData?.getDateLimit()?.toDate()
    if (!date) return
    setDateLimit(date)
  }, [dateLimitData, setDateLimit])

  return {
    ...useContext(AuditScanModalContext),
    isLoading:
      isAllControlsLoading || isAuditReadinessLoading || isDateLimitLoading,
  }
}

export const useAuditScanModal = (
  calculateStaleness: boolean,
): (({
  title,
  description,
  selectedTests,
  controlsFilter,
  controlsPreselect,
  onBackModalId,
  singleControlId,
  displayConfig,
  dateRange,
}: UseAuditScanModalType) => void) => {
  const { openModal } = useModalState()
  const {
    setAuditId,
    setTitle,
    setDescription,
    setSelectedTests,
    setControlsFilter,
    setControlsPreselect,
    setOnBackModalId,
    setSingleControlId,
    setDisplayConfig,
    setDateRange,
    dateLimit,
  } = useAuditScanModalContext(calculateStaleness)

  return useCallback(
    ({
      auditId,
      title,
      description,
      selectedTests,
      controlsFilter,
      controlsPreselect,
      onBackModalId,
      singleControlId,
      displayConfig,
      dateRange,
    }: UseAuditScanModalType) => {
      if (auditId) {
        setAuditId(auditId)
      }
      setTitle(title)
      setDescription(description)
      selectedTests && setSelectedTests(selectedTests)
      setControlsFilter(controlsFilter || [ControlDisplay.ALL])
      setControlsPreselect(controlsPreselect || [ControlDisplay.ALL])
      setDisplayConfig(
        displayConfig || {
          buttonConfig: ButtonConfig.DEFAULT,
          showResults: false,
        },
      )
      if (dateRange) {
        setDateRange(dateRange)
      }
      if (!auditId && !dateRange) {
        setDateRange({
          startDate: dateLimit,
          endDate: new Date(),
        })
      }
      setSingleControlId(singleControlId)
      openModal(ModalFormId.AUDIT_SCAN_MODAL)
      setOnBackModalId(onBackModalId || null)
    },
    [
      setAuditId,
      openModal,
      setControlsFilter,
      setControlsPreselect,
      setDescription,
      setOnBackModalId,
      setSelectedTests,
      setTitle,
      setSingleControlId,
      setDisplayConfig,
      setDateRange,
      dateLimit,
    ],
  )
}

export const useRoadmapModal = (): (({
  controlName,
  controlsFilter,
  controlsPreselect,
  selectedTests,
  singleControlId,
}: Partial<UseAuditScanModalType>) => void) => {
  const auditScanModal = useAuditScanModal(true)
  const { auditId } = useInAudit()

  return useCallback(
    ({
      controlName,
      controlsFilter,
      controlsPreselect,
      selectedTests,
      singleControlId,
    }: Partial<UseAuditScanModalType>) =>
      auditScanModal({
        title:
          singleControlId && controlName
            ? controlName
            : getRoadmapModalTitle(controlName || 'Audit Readiness'),
        description: ROADMAP_MODAL.DESCRIPTION,
        selectedTests,
        controlsFilter,
        auditId: auditId || '',
        controlsPreselect,
        singleControlId,
        displayConfig: singleControlId
          ? { buttonConfig: ButtonConfig.NONE, showResults: true }
          : { buttonConfig: ButtonConfig.ALL, showResults: true },
      }),
    [auditScanModal, auditId],
  )
}

export const useAuditBotModal = (): (({
  auditId,
  onBackModalId,
  range,
}: Partial<UseAuditScanModalType> & {
  auditId: string | undefined
  range?: DateRange
}) => void) => {
  const auditScanModal = useAuditScanModal(false)

  return useCallback(
    ({ onBackModalId, auditId, range }) =>
      auditScanModal({
        title: 'Select Controls to Test',
        description: <AuditBotControlsDescription />,
        onBackModalId,
        auditId,
        selectedTests: [],
        displayConfig: {
          buttonConfig: ButtonConfig.DEFAULT,
          showResults: false,
        },
        dateRange: range,
      }),
    [auditScanModal],
  )
}

export const useFilteredControlIds = ({
  auditId,
  filters,
  controlCheckNames,
}: {
  auditId?: string
  filters: ControlDisplay[]
  controlCheckNames?: CONTROL_TEST_NAMES[]
}): string[] => {
  const relevantTests = controlCheckNames || [
    CONTROL_TEST_NAMES.COMPLETENESS,
    CONTROL_TEST_NAMES.POLICY_MATCH,
    CONTROL_TEST_NAMES.SPOT_CHECK,
  ]
  const { data: controlsData, isLoading: controlsLoading } =
    useControlsWithAuditId(auditId)
  const { data, isLoading } = useAuditReadiness({
    auditId,
    relevantTests,
    calculateStaleness: true,
  })
  if (isLoading || !data || controlsLoading || !controlsData) return []
  const auditReadinessList = data.getAuditReadinessList()
  const controls = controlsData.getItemsList()
  const auditReadinessMap = normalizeByControlId(auditReadinessList)
  const controlTestMap = createControlTestMap(relevantTests, auditReadinessMap)
  const filteredControlIds = getFilteredControlIds(
    controls,
    controlTestMap,
    filters,
  )
  return filteredControlIds
}
